import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { listMyOffers, acceptOffer, declineOffer, listMyHelperBookings, getHelperMetrics } from "@/lib/bookings.functions";
import { formatGBP } from "@/lib/pricing";
import { track } from "@/lib/funnel-track";
import { Loader2, Clock, MapPin, CheckCircle2, XCircle, Banknote, Star, TrendingUp, Wallet, Zap, BadgeCheck, ChevronRight } from "lucide-react";

export const Route = createFileRoute("/_authenticated/helper/offers")({
  head: () => ({ meta: [{ title: "Helper dashboard — Peopels" }, { name: "robots", content: "noindex" }] }),
  component: HelperOffers,
});

type Offer = {
  id: string;
  status: string;
  score: number | null;
  expires_at: string;
  created_at: string;
  job: { id: string; title: string; description: string; postcode: string; urgency: string | null; scheduled_at: string | null; budget_min: number | null; budget_max: number | null } | null;
};

type HelperBooking = {
  id: string; status: string; payment_status: string | null;
  payout_status: string | null; payout_amount: number | null; payout_completed_at: string | null;
  payout_failure_reason: string | null; helper_payout: number | null; total_amount: number | null;
  scheduled_start: string | null; job: { id: string; title: string; postcode: string } | null;
};

type Metrics = Awaited<ReturnType<typeof getHelperMetrics>>;

function HelperOffers() {
  const navigate = useNavigate();
  const list = useServerFn(listMyOffers);
  const accept = useServerFn(acceptOffer);
  const decline = useServerFn(declineOffer);
  const fetchBookings = useServerFn(listMyHelperBookings);
  const fetchMetrics = useServerFn(getHelperMetrics);
  const [offers, setOffers] = useState<Offer[]>([]);
  const [bookings, setBookings] = useState<HelperBooking[]>([]);
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const [o, b, m] = await Promise.all([list(), fetchBookings(), fetchMetrics()]);
      setOffers(o.offers as unknown as Offer[]);
      setBookings(b.bookings as unknown as HelperBooking[]);
      setMetrics(m as Metrics);
    } catch { /* ignored */ }
  }, [list, fetchBookings, fetchMetrics]);

  useEffect(() => {
    (async () => {
      const { data } = await supabase.auth.getUser();
      if (!data.user) { navigate({ to: "/auth", search: { redirect: "/helper/offers" } }); return; }
      setUserId(data.user.id);
      await refresh();
      setLoading(false);
    })();
  }, [navigate, refresh]);

  useEffect(() => {
    if (!userId) return;
    const channel = supabase
      .channel(`helper-offers:${userId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "job_offers", filter: `helper_id=eq.${userId}` }, () => void refresh())
      .on("postgres_changes", { event: "*", schema: "public", table: "bookings", filter: `helper_id=eq.${userId}` }, () => void refresh())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [userId, refresh]);

  // Track that pending offers were seen — enables "missed offers" analytics
  useEffect(() => {
    offers.filter((o) => o.status === "pending").forEach((o) => { void track("offer_view", { offer_id: o.id, job_id: o.job?.id }); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offers.length]);


  async function onAccept(id: string) {
    try {
      await track("offer_accept", { offer_id: id });
      const res = await accept({ data: { offerId: id } });
      if (res.status === "accepted") toast.success("Booking created — customer is paying now.");
      else if (res.status === "already_taken") toast.message("Another Helper got there first.");
      else if (res.status === "expired") toast.message("That offer expired.");
      else toast.message(`Offer is ${res.status}.`);
      await refresh();
    } catch (err) { toast.error(err instanceof Error ? err.message : "Could not accept"); }
  }

  async function onDecline(id: string) {
    try { await track("offer_decline", { offer_id: id }); await decline({ data: { offerId: id } }); await refresh(); } catch (err) { toast.error(err instanceof Error ? err.message : "Could not decline"); }
  }

  return (
    <div className="flex min-h-screen flex-col bg-surface">
      <Header />
      <main id="main-content" className="mx-auto w-full max-w-5xl flex-1 px-4 py-8 sm:px-6 sm:py-10 lg:px-8">
        <header className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 sm:flex sm:flex-wrap sm:items-end sm:justify-between">
          <div className="min-w-0">
            <p className="text-sm text-muted-foreground">Helper dashboard</p>
            <h1 className="truncate font-display text-2xl font-bold sm:text-3xl">Your performance</h1>
          </div>
          <Button asChild variant="outline" size="sm" className="shrink-0 rounded-full">
            <Link to="/helpers/$helperId" params={{ helperId: userId ?? "" }}>View public profile</Link>
          </Button>
        </header>

        {loading ? (
          <div className="mt-12 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : (
          <>
            {/* Earnings & performance stats */}
            {metrics && (
              <>
                <div className="mt-6 grid grid-cols-2 gap-3 sm:mt-8 sm:grid-cols-4 sm:gap-4">
                  <StatCard icon={Wallet} label="This week" value={formatGBP(metrics.earnings.week)} tone="emerald" />
                  <StatCard icon={TrendingUp} label="This month" value={formatGBP(metrics.earnings.month)} tone="blue" />
                  <StatCard icon={Banknote} label="Pending payout" value={formatGBP(metrics.earnings.pendingPayout)} tone="amber" />
                  <StatCard icon={CheckCircle2} label="Completed jobs" value={String(metrics.performance.completedJobs)} tone="primary" />
                </div>

                <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4">
                  <MiniStat icon={Star} label="Rating" value={metrics.performance.avgRating ? metrics.performance.avgRating.toFixed(2) : "—"} sub={`${metrics.performance.ratingCount} reviews`} />
                  <MiniStat icon={Zap} label="Response rate" value={`${metrics.performance.responseRate}%`} sub="last 30 days" />
                  <MiniStat icon={CheckCircle2} label="Accept rate" value={`${metrics.performance.acceptRate}%`} sub="last 30 days" />
                  <MiniStat icon={Clock} label="Avg response" value={metrics.performance.responseTimeMinutes ? `${metrics.performance.responseTimeMinutes}m` : "—"} sub="time to reply" />
                </div>

                {/* Profile completion */}
                {metrics.checklist.percent < 100 && (
                  <section className="mt-6 rounded-2xl border border-border bg-card p-5 shadow-card sm:p-6">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <h2 className="font-display text-base font-semibold sm:text-lg">Complete your profile</h2>
                        <p className="text-xs text-muted-foreground">A complete profile helps customers choose you with confidence.</p>
                      </div>
                      <span className="shrink-0 font-display text-xl font-bold sm:text-2xl">{metrics.checklist.percent}%</span>
                    </div>
                    <div className="mt-3 h-2 overflow-hidden rounded-full bg-muted">
                      <div className="h-full rounded-full bg-gradient-to-r from-primary to-emerald-500 transition-all" style={{ width: `${metrics.checklist.percent}%` }} />
                    </div>
                    <ul className="mt-4 grid gap-2 sm:grid-cols-2">
                      {metrics.checklist.items.map((c) => (
                        <li key={c.key} className="flex items-center gap-2 text-sm">
                          {c.done
                            ? <BadgeCheck className="h-4 w-4 shrink-0 text-emerald-600" />
                            : <span className="h-4 w-4 shrink-0 rounded-full border-2 border-muted-foreground/40" />}
                          <span className={c.done ? "text-muted-foreground line-through" : "text-foreground"}>{c.label}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-4 flex flex-wrap gap-2">
                      <Button asChild size="sm" variant="outline" className="rounded-full"><Link to="/become-helper">Edit profile <ChevronRight className="ml-0.5 h-3 w-3" /></Link></Button>
                      {!metrics.profile?.id_verified && (
                        <Button asChild size="sm" className="rounded-full"><Link to="/helper/kyc">Verify ID</Link></Button>
                      )}
                    </div>
                  </section>
                )}
              </>
            )}

            {/* Incoming offers */}
            <section className="mt-8 sm:mt-10">
              <div className="mb-3 flex items-center justify-between gap-3">
                <h2 className="font-display text-lg font-semibold sm:text-xl">Incoming offers</h2>
                <span className="text-xs text-muted-foreground">First to accept wins</span>
              </div>
              {offers.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-border bg-card p-8 text-center text-sm text-muted-foreground">
                  No pending offers right now. We'll notify you the moment one arrives.
                </div>
              ) : (
                <ul className="space-y-3">
                  {offers.map((o) => (
                    <li key={o.id} className="rounded-2xl border border-border bg-card p-4 shadow-card sm:p-5">
                      <div className="grid grid-cols-[minmax(0,1fr)] gap-3 sm:grid-cols-[minmax(0,1fr)_auto]">
                        <div className="min-w-0">
                          <p className="truncate font-display font-semibold">{o.job?.title}</p>
                          <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{o.job?.description}</p>
                          <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                            <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{o.job?.postcode}</span>
                            {o.job?.scheduled_at && <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{new Date(o.job.scheduled_at).toLocaleString("en-GB")}</span>}
                            {o.job?.urgency && o.job.urgency !== "normal" && <span className="rounded-full bg-amber-100 px-2 py-0.5 capitalize text-amber-900">{o.job.urgency}</span>}
                            <OfferCountdown expiresAt={o.expires_at} />
                          </div>
                        </div>
                        {o.status === "pending" ? (
                          <div className="flex gap-2 sm:flex-col sm:items-end">
                            <Button variant="outline" className="flex-1 sm:flex-none" onClick={() => onDecline(o.id)}><XCircle className="mr-1 h-4 w-4" />Pass</Button>
                            <Button className="flex-1 sm:flex-none" onClick={() => onAccept(o.id)}><CheckCircle2 className="mr-1 h-4 w-4" />Accept</Button>
                          </div>
                        ) : (
                          <span className="self-start rounded-full bg-accent px-2.5 py-1 text-xs font-medium capitalize text-primary">{o.status}</span>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* Bookings & payouts */}
            <section className="mt-10">
              <h2 className="mb-3 inline-flex items-center gap-2 font-display text-lg font-semibold sm:text-xl">
                <Banknote className="h-5 w-5" /> Bookings & payouts
              </h2>
              {bookings.length === 0 ? (
                <p className="rounded-2xl border border-dashed border-border bg-card p-6 text-center text-sm text-muted-foreground">No bookings yet.</p>
              ) : (
                <ul className="divide-y divide-border overflow-hidden rounded-2xl border border-border bg-card">
                  {bookings.map((b) => (
                    <li key={b.id} className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3 p-4 sm:p-5">
                      <div className="min-w-0">
                        <p className="truncate font-medium">{b.job?.title ?? "Booking"}</p>
                        <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                          <span>{b.job?.postcode}</span>
                          {b.scheduled_start && <span className="hidden sm:inline">{new Date(b.scheduled_start).toLocaleString("en-GB")}</span>}
                          {b.helper_payout != null && <span className="font-medium text-foreground">Payout {formatGBP(Number(b.helper_payout))}</span>}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <PayoutBadge status={b.payout_status} payment={b.payment_status} />
                        {b.payout_failure_reason && <span className="text-right text-xs text-destructive">{b.payout_failure_reason}</span>}
                        {b.payout_completed_at && <span className="text-xs text-muted-foreground">Paid {new Date(b.payout_completed_at).toLocaleDateString("en-GB")}</span>}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}

function OfferCountdown({ expiresAt }: { expiresAt: string }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const ms = new Date(expiresAt).getTime() - now;
  if (ms <= 0) return <span className="rounded-full bg-red-100 px-2 py-0.5 font-medium text-red-900">Expired</span>;
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  const urgent = ms < 5 * 60_000;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-mono font-medium tabular-nums ${urgent ? "bg-red-100 text-red-900 animate-pulse" : "bg-amber-100 text-amber-900"}`}>
      <Clock className="h-3 w-3" />{m}:{s.toString().padStart(2, "0")} left
    </span>
  );
}

function StatCard({ icon: Icon, label, value, tone }: { icon: typeof Wallet; label: string; value: string; tone: "emerald" | "blue" | "amber" | "primary" }) {
  const toneMap = {
    emerald: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
    blue: "bg-blue-500/10 text-blue-700 dark:text-blue-300",
    amber: "bg-amber-500/10 text-amber-700 dark:text-amber-300",
    primary: "bg-accent text-primary",
  };
  return (
    <div className="rounded-2xl border border-border bg-card p-4 shadow-card sm:p-5">
      <div className="flex items-center gap-3">
        <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-xl sm:h-10 sm:w-10 ${toneMap[tone]}`}><Icon className="h-4 w-4 sm:h-5 sm:w-5" /></span>
        <div className="min-w-0">
          <p className="truncate text-[10px] uppercase tracking-wider text-muted-foreground sm:text-xs">{label}</p>
          <p className="font-display text-lg font-bold sm:text-2xl">{value}</p>
        </div>
      </div>
    </div>
  );
}

function MiniStat({ icon: Icon, label, value, sub }: { icon: typeof Star; label: string; value: string; sub: string }) {
  return (
    <div className="rounded-xl border border-border bg-card p-3 sm:p-4">
      <p className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground sm:text-xs"><Icon className="h-3 w-3" /> {label}</p>
      <p className="mt-1 font-display text-base font-bold sm:text-lg">{value}</p>
      <p className="text-[10px] text-muted-foreground sm:text-xs">{sub}</p>
    </div>
  );
}

function PayoutBadge({ status, payment }: { status: string | null; payment: string | null }) {
  if (payment !== "paid" && status !== "paid") {
    return <span className="rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">Awaiting payment</span>;
  }
  const map: Record<string, string> = {
    pending: "bg-amber-100 text-amber-900",
    in_transit: "bg-blue-100 text-blue-900",
    paid: "bg-emerald-100 text-emerald-900",
    failed: "bg-red-100 text-red-900",
    reversed: "bg-slate-200 text-slate-800",
  };
  const label: Record<string, string> = {
    pending: "Payout pending",
    in_transit: "Payout in transit",
    paid: "Payout succeeded",
    failed: "Payout failed",
    reversed: "Refunded",
  };
  const key = status ?? "pending";
  return <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${map[key] ?? map.pending}`}>{label[key] ?? "Pending"}</span>;
}
